import React, { useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Alert,
} from "react-bootstrap";
import { Input, FormGroup } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata.js";

const STREET_DIRECTIONS = ["None", "N", "S", "E", "W", "NE", "NW", "SE", "SW"];

const CANADIAN_PROVINCES = [
  { value: "", label: "Select Province..." },
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

const initialData = {
  // End Customer Address
  end_customer_name: "",
  end_customer_unit: "",
  end_customer_street_number: "",
  end_customer_street_name: "",
  end_customer_street_type: "",
  end_customer_street_suffix: "",
  end_customer_street_direction: "None",
  end_customer_floor: "",
  end_customer_city: "",
  end_customer_country: "CA",
  end_customer_province: "",
  end_customer_postal_code: "",
  // Account Info
  end_customer_account_number: "",
  current_provider: "",
  customer_reference_number: "",
  // Numbers
  numbers_to_port: "",
  numbers_to_remain: "",
  numbers_to_disconnect: "",
  // Yes/No flags
  pilot_porting: "",
  has_pri_circuit: "",
  business_listing_required: "",
  specific_port_date: "",
  specific_port_time: "",
  //Yes/No additional fields
  replacement_pilot_number: "",
  pri_circuit_number: "",
  pri_remain_active: "",      // "remain" | "disconnect"
  specific_port_date_value: "",
  specific_port_time_value: "",
  // Comments
  comments: "",
  // File
  additional_docs: null,
};

const DIDImport = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const fileInputRef = useRef(null);
  const domain_uuid = localStorage.getItem("clientId");
  const [formData, setFormData] = useState(initialData);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fileName, setFileName] = useState("");

  const labelStyle = {
    fontWeight: "bolder",
    color: isDark ? "#e2e8f0" : "#1e3a5f",
    marginBottom: "0.5rem",
  };

  const sectionHeaderStyle = {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: isDark ? "#60a5fa" : "#2563eb",
    borderBottom: `1px solid ${isDark ? "#1e3a5f" : "#bfdbfe"}`,
    paddingBottom: "0.4rem",
    marginBottom: "1rem",
    marginTop: "1.5rem",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, additional_docs: file }));
      setFileName(file.name);
    }
  };

  const handleSave = async () => {
    setError("");

    // Validate required fields
    const required = [
      "end_customer_name",
      "end_customer_street_number",
      "end_customer_street_name",
      "end_customer_city",
      "end_customer_province",
      "end_customer_postal_code",
      "end_customer_account_number",
      "current_provider",
      "numbers_to_port",
    ];

    const missing = required.filter(
      (f) => !formData[f] || formData[f].toString().trim() === ""
    );

    if (missing.length > 0) {
      setError(
        `Missing required fields: ${missing
          .map((f) => f.replace(/_/g, " "))
          .join(", ")}`
      );
      return;
    }

    sessionStorage.setItem("pendingDIDs", JSON.stringify(formData));
    navigate("/admin/subscription");
  };

  const handleBack = () => {
    const isDirty = Object.entries(formData).some(
      ([key, val]) =>
        key !== "additional_docs" &&
        val !== "" &&
        val !== null &&
        val !== "None" &&
        val !== "CA"
    );
    if (!isDirty || window.confirm("Discard changes and go back?")) {
      navigate(-1);
    }
  };

  const YesNoField = ({ label, name, required }) => (
    <Form.Group className="mb-3">
      <Form.Label style={labelStyle}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Form.Label>
      <div className="d-flex gap-3">
        {["yes", "no"].map((opt) => (
          <Form.Check
            key={opt}
            inline
            type="radio"
            label={opt.charAt(0).toUpperCase() + opt.slice(1)}
            name={name}
            value={opt}
            checked={formData[name] === opt}
            onChange={handleChange}
          />
        ))}
      </div>
    </Form.Group>
  );

  return (
    <>
      <SimpleHeader name="Port Request" parentName="HUCS" />

      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#e9ecef",
              color: isDark ? "#e2e8f0" : "#1e3a5f",
              borderBottom: "none",
              padding: "1rem 1.5rem",
            }}
          >
          </Card.Header>

          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form>

              {/* ── END CUSTOMER INFO ── */}
              <div style={sectionHeaderStyle}>End Customer Information</div>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      End Customer Name <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_name"
                      value={formData.end_customer_name}
                      onChange={handleChange}
                      placeholder="Legal business name on current carrier's invoice"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Unit</Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_unit"
                      value={formData.end_customer_unit}
                      onChange={handleChange}
                      placeholder="e.g. Suite 200"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Floor</Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_floor"
                      value={formData.end_customer_floor}
                      onChange={handleChange}
                      placeholder="e.g. 3"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* ── END CUSTOMER ADDRESS ── */}
              <div style={sectionHeaderStyle}>End Customer Service Address</div>

              <Row>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Street Number <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_street_number"
                      value={formData.end_customer_street_number}
                      onChange={handleChange}
                      placeholder="123"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Street Name <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_street_name"
                      value={formData.end_customer_street_name}
                      onChange={handleChange}
                      placeholder="Main"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Street Type</Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_street_type"
                      value={formData.end_customer_street_type}
                      onChange={handleChange}
                      placeholder="St, Ave, Blvd..."
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Street Suffix</Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_street_suffix"
                      value={formData.end_customer_street_suffix}
                      onChange={handleChange}
                      placeholder="e.g. SW"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Street Direction</Form.Label>
                    <Input
                      type="select"
                      name="end_customer_street_direction"
                      value={formData.end_customer_street_direction}
                      onChange={handleChange}
                    >
                      {STREET_DIRECTIONS.map((dir) => (
                        <option key={dir} value={dir}>
                          {dir}
                        </option>
                      ))}
                    </Input>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      City <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_city"
                      value={formData.end_customer_city}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Province <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Input
                      type="select"
                      name="end_customer_province"
                      value={formData.end_customer_province}
                      onChange={handleChange}
                    >
                      {CANADIAN_PROVINCES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </Input>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Postal Code <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_postal_code"
                      value={formData.end_customer_postal_code}
                      onChange={handleChange}
                      placeholder="V6B 1A1"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Country</Form.Label>
                    <Input
                      type="select"
                      name="end_customer_country"
                      value={formData.end_customer_country}
                      onChange={handleChange}
                    >
                      <option value="CA">Canada</option>
                      <option value="US">United States</option>
                    </Input>
                  </Form.Group>
                </Col>
              </Row>

              {/* ── ACCOUNT INFO ── */}
              <div style={sectionHeaderStyle}>Account & Provider Information</div>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Account Number <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="end_customer_account_number"
                      value={formData.end_customer_account_number}
                      onChange={handleChange}
                      placeholder="Account # with current carrier"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Current Provider <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="current_provider"
                      value={formData.current_provider}
                      onChange={handleChange}
                      placeholder="e.g. Telus, Bell, Shaw"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Customer Reference Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="customer_reference_number"
                      value={formData.customer_reference_number}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* ── NUMBERS ── */}
              <div style={sectionHeaderStyle}>Number Details</div>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Number(s) to Port <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="numbers_to_port"
                      value={formData.numbers_to_port}
                      onChange={handleChange}
                      placeholder="One number per line&#10;6045551001&#10;6045551002"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Number(s) to Remain Active
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="numbers_to_remain"
                      value={formData.numbers_to_remain}
                      onChange={handleChange}
                      placeholder="Numbers staying with current carrier"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Number(s) to Disconnect
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="numbers_to_disconnect"
                      value={formData.numbers_to_disconnect}
                      onChange={handleChange}
                      placeholder="Numbers to cancel entirely"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* ── PORT CONFIGURATION ── */}
<div style={sectionHeaderStyle}>Port Configuration</div>

<Row>
  <Col md={6}>
    <YesNoField
      label="Is the pilot number porting and some dependent numbers remaining with the existing carrier?"
      name="pilot_porting"
    />
    {formData.pilot_porting === "yes" && (
      <Form.Group className="mb-3">
        <Form.Label style={labelStyle}>Replacement Pilot Number</Form.Label>
        <Form.Control
          type="text"
          name="replacement_pilot_number"
          value={formData.replacement_pilot_number}
          onChange={handleChange}
          placeholder="e.g. 6045551000"
        />
      </Form.Group>
    )}
  </Col>
  <Col md={6}>
    <YesNoField
      label="Is there a PRI / Megalink Circuit #?"
      name="has_pri_circuit"
    />
    {formData.has_pri_circuit === "yes" && (
      <>
        <Form.Group className="mb-3">
          <Form.Label style={labelStyle}>PRI / Megalink Circuit #</Form.Label>
          <Form.Control
            type="text"
            name="pri_circuit_number"
            value={formData.pri_circuit_number}
            onChange={handleChange}
            placeholder="Circuit number"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label style={labelStyle}>Circuit Status</Form.Label>
          <div className="d-flex gap-3">
            {["remain", "disconnect"].map((opt) => (
              <Form.Check
                key={opt}
                inline
                type="radio"
                label={opt === "remain" ? "Remain Active" : "Disconnect"}
                name="pri_remain_active"
                value={opt}
                checked={formData.pri_remain_active === opt}
                onChange={handleChange}
              />
            ))}
          </div>
        </Form.Group>
      </>
    )}
  </Col>
</Row>

<Row>
  <Col md={6}>
    <YesNoField
      label="Does this request require a specific port date?"
      name="specific_port_date"
    />
    {formData.specific_port_date === "yes" && (
      <Form.Group className="mb-3">
        <Form.Label style={labelStyle}>Port Date (ET)</Form.Label>
        <Form.Control
          type="date"
          name="specific_port_date_value"
          value={formData.specific_port_date_value}
          onChange={handleChange}
        />
      </Form.Group>
    )}
  </Col>
  <Col md={6}>
    <YesNoField
      label="Does this request require a specific port time?"
      name="specific_port_time"
    />
    {formData.specific_port_time === "yes" && (
      <>
        <Form.Group className="mb-3">
          <Form.Label style={labelStyle}>Port Time (ET)</Form.Label>
          <Form.Control
            type="time"
            name="specific_port_time_value"
            value={formData.specific_port_time_value}
            onChange={handleChange}
          />
        </Form.Group>
        <div
          style={{
            fontSize: "0.78rem",
            border: `1px solid ${isDark ? "#374151" : "#d1d5db"}`,
            borderRadius: "0.4rem",
            padding: "0.6rem 0.8rem",
            backgroundColor: isDark ? "#111827" : "#f9fafb",
            color: isDark ? "#9ca3af" : "#6b7280",
            lineHeight: "1.6",
          }}
        >
          <strong style={{ color: isDark ? "#60a5fa" : "#2563eb" }}>
            Timed Port Fee Schedule
          </strong>
          <table style={{ width: "100%", marginTop: "0.4rem", borderCollapse: "collapse" }}>
            <tbody>
              {[
                ["09:00 - 17:00 Mon-Fri", "No Additional Fee"],
                ["17:00 - 21:00 Mon-Fri", "$150.00"],
                ["21:00 - 09:00 Mon-Thu", "$350.00"],
                ["21:00 - 09:00 Fri-Mon", "$550.00"],
              ].map(([time, fee]) => (
                <tr key={time}>
                  <td style={{ paddingRight: "1rem", paddingTop: "0.15rem" }}>{time}</td>
                  <td style={{ fontWeight: 600, color: isDark ? "#e2e8f0" : "#374151" }}>{fee}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "0.5rem", fontStyle: "italic", fontSize: "0.72rem" }}>
            All times EST/EDT. Charges based on time of completion. Holidays charged at weekend rate. Pending confirmation of available resources.
          </div>
        </div>
      </>
    )}
  </Col>
</Row>

<Row>
  <Col md={6}>
    <YesNoField
      label="Business Listing Required? (If yes, please fill out 411 spreadsheet)"
      name="business_listing_required"
    />
  </Col>
</Row>

              {/* ── COMMENTS & DOCS ── */}
              <div style={sectionHeaderStyle}>Additional Information</div>

              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Comments for the Porting Team
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="comments"
                      value={formData.comments}
                      onChange={handleChange}
                      placeholder="Any special instructions, notes, or context for the ThinkTel porting team..."
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>
                      Additional Documentation
                    </Form.Label>
                    <div
                      style={{
                        border: `2px dashed ${isDark ? "#374151" : "#d1d5db"}`,
                        borderRadius: "0.5rem",
                        padding: "1rem",
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: isDark ? "#111827" : "#f9fafb",
                        minHeight: "6rem",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i
                        className="fas fa-upload"
                        style={{ color: isDark ? "#60a5fa" : "#3b82f6", fontSize: "1.25rem" }}
                      />
                      {fileName ? (
                        <small style={{ color: isDark ? "#e2e8f0" : "#374151", wordBreak: "break-all" }}>
                          {fileName}
                        </small>
                      ) : (
                        <small style={{ color: isDark ? "#9ca3af" : "#6b7280" }}>
                          Click to upload LOA, invoice, or other docs
                        </small>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: "none" }}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg"
                      onChange={handleFileChange}
                    />
                    {fileName && (
                      <small
                        style={{ color: isDark ? "#f87171" : "#ef4444", cursor: "pointer" }}
                        onClick={() => {
                          setFileName("");
                          setFormData((prev) => ({ ...prev, additional_docs: null }));
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                      >
                        ✕ Remove file
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={handleBack}>
                  Cancel
                </Button>
                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={
                    submitting ||
                    !formData.end_customer_name ||
                    !formData.end_customer_street_number ||
                    !formData.end_customer_street_name ||
                    !formData.end_customer_city ||
                    !formData.end_customer_province ||
                    !formData.end_customer_postal_code ||
                    !formData.end_customer_account_number ||
                    !formData.current_provider ||
                    !formData.numbers_to_port
                  }
                >
                  {submitting ? "Submitting..." : "Submit Port Request"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default DIDImport;